import { Delete } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import DataDisplay from 'shared/components/data-display/DataDisplay';
import HTML from 'shared/components/HTML';
import CancelConfirm from 'shared/components/dialog/CancelConfirm';
import { useAuth } from 'shared/hooks';
import NoteInput from './NoteInput';

/**
 * A crude way of deep copying an array to avoid mutating array props.
 * @param {Array<Object>} arr
 * @returns {Array<Object>}
 */
export const crudeCloneDeep = (arr) => JSON.parse(JSON.stringify(arr));

/**
 * @param {Object} props - Component props
 * @param {Array<Object>} props.initialNotes - Array of initial note objects
 * @param {boolean} props.open - Whether the editor is open
 * @param {Function} props.onUpdateNote - Handler for updating a note
 * @param {Function} props.onDeleteNote - Handler for deleting a note
 * @param {Function} props.setDirty - Callback to set dirty state
 * @param {string} [props.legacyNotes=''] - Legacy notes text
 * @param {Function} [props.onDeleteLegacyNotes] - Handler for deleting legacy notes
 * @returns {JSX.Element}
 */
export default function NotesEditor({
  initialNotes,
  open,
  onUpdateNote,
  onDeleteNote,
  setDirty,
  legacyNotes = '',
  onDeleteLegacyNotes = () => {},
}) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(crudeCloneDeep(initialNotes));
  const [deleteCandidate, setDeleteCandidate] = useState(undefined);
  const [deleteLegacy, setDeleteLegacy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNotes(crudeCloneDeep(initialNotes));
  }, [initialNotes, open]);

  const handleSaveNote = (note, value) => {
    const update = note;
    update.note_type.employee_id = user.id;
    update.employee_id = user.id;
    update.value = value;
    onUpdateNote(update);
  };

  return (
    <Grid container spacing={0}>
      {notes.map((note) => (
        <Grid size={12} key={note.id}>
          <NoteInput
            note={note}
            onSaveNote={(value) => handleSaveNote(note, value)}
            onDeleteNote={setDeleteCandidate}
            setDirty={setDirty}
          />
        </Grid>
      ))}
      {legacyNotes && (
      <Grid
        size={12}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          '& > .MuiFormControl-root': { flex: 1, minWidth: 0 },
        }}
      >
        <DataDisplay
          value={<HTML content={legacyNotes} />}
          label="Legacy Notes"
          copyable
          multiline
        />
        <Box sx={{ height: 'fit-content', alignContent: 'center' }}>
          <TooltipIconButton
            message="Delete legacy notes"
            placement="left"
            icon={Delete}
            onClick={() => { setDeleteLegacy(true); }}
          />

        </Box>
      </Grid>
      )}
      <CancelConfirm
        open={deleteCandidate !== undefined}
        title="Delete note?"
        text="Are you sure you want to delete this note?"
        cancelText="Back"
        confirmText="Delete"
        onCancel={() => { setDeleteCandidate(undefined); }}
        onConfirm={() => {
          onDeleteNote(deleteCandidate);
          setDeleteCandidate(undefined);
        }}
      />
      {legacyNotes && (
      <CancelConfirm
        open={deleteLegacy}
        title="Delete legacy notes?"
        text="Are you sure you want to delete legacy notes?"
        cancelText="Back"
        confirmText="Delete"
        onCancel={() => { setDeleteLegacy(false); }}
        onConfirm={() => {
          onDeleteLegacyNotes();
          setDeleteLegacy(false);
        }}
      />
      )}
    </Grid>
  );
}
